import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Button } from '~Styles';
import { Logo } from '~Scenes/Gateway/styles';
import bLogo from '~Assets/Logos/b_logo_black.svg';
import { useButton, useWindowSize } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import ArrowForwardIcon from '~Assets/icons/arrow_forward.svg';
import ArrowBackIcon from '~Assets/icons/arrow_back.svg';
import FullscreenIcon from '~Assets/icons/fullscreen.svg';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import { insideWrapperStyles } from '~ComponentsImproved/Modal';

const NoteImage = styled.img`
  width: 128px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0; /* Prevent image from shrinking */
  /* Aspect ratio 16:10 to match 1280x800px images */
`;

const ImageNavigationContainer = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
`;

const LogoContainer = styled.div`
  width: 96px;
  height: 48px;
  background-color: var(--colorBlack);
  padding: 16px;
  border-radius: 8px;
  flex-shrink: 0; /* Prevent logo from shrinking when text is long */
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FullscreenButton = styled(Button)`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: var(--colorBlueGray1300);
  padding: 8px;
  border-radius: 4px;
  z-index: 3;
  opacity: 0.7;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

const ModalWrapper = styled(Box)`
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  position: relative;
`;

const FullsizeImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
`;

const ModalNavigationButton = styled(Button)<{ position: 'left' | 'right'; disabled?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  opacity: ${(props) => (props.disabled ? 0.2 : 0.7)};
  ${(props) => (props.position === 'left' ? 'left: 16px;' : 'right: 16px;')}
  background-color: var(--colorBlueGray1300);
  padding: 8px;
  border-radius: 4px;
  z-index: 4;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

  &:hover {
    opacity: ${(props) => (props.disabled ? 0.2 : 1)};
  }
`;

interface Props {
  imageUrls: string[];
}

/**
 * Shared component for displaying release images with navigation
 * Used by both Feature and ReleaseNotes components
 */
const ReleaseImage: FC<Props> = ({ imageUrls }) => {
  const windowSize = useWindowSize();
  const fullscreenModal = useModal();

  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : imageUrls.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < imageUrls.length - 1 ? prev + 1 : 0));
  };

  const navigateLeftButton = useButton({
    onClick: handlePrevImage,
    disabled: currentImageIndex === 0,
  });

  const navigateRightButton = useButton({
    onClick: handleNextImage,
    disabled: currentImageIndex === imageUrls.length - 1,
  });

  const fullscreenButton = useButton({
    onClick: fullscreenModal.show,
  });

  const showModalNavigation = imageUrls.length > 1;
  const currentImage = imageUrls.length > 0 ? imageUrls[currentImageIndex] : null;

  /** Under 944 shows the releases in a modal where the is space enough */
  const isSmallWidthTablet = windowSize.width > 944 && windowSize.width < 1168;

  // Hide on small tablets
  if (isSmallWidthTablet) {
    return null;
  }

  // Show current image with navigation if available
  if (currentImage) {
    return (
      <>
        <ImageNavigationContainer>
          <NoteImage src={`${currentImage}/1280x800`} alt="Release note" />
          <FullscreenButton {...fullscreenButton}>
            <Icon src={FullscreenIcon} height="20px" width="20px" color="var(--colorWhite)" />
          </FullscreenButton>
        </ImageNavigationContainer>

        <Modal isShowing={fullscreenModal.isShowing} hide={fullscreenModal.hide}>
          <ModalWrapper>
            <FullsizeImage src={`${currentImage}/1280x800`} alt="Release note fullscreen" />
            {showModalNavigation && (
              <>
                <ModalNavigationButton position="left" {...navigateLeftButton}>
                  <Icon src={ArrowBackIcon} height="24px" width="24px" color="var(--colorWhite)" />
                </ModalNavigationButton>
                <ModalNavigationButton position="right" {...navigateRightButton}>
                  <Icon
                    src={ArrowForwardIcon}
                    height="24px"
                    width="24px"
                    color="var(--colorWhite)"
                  />
                </ModalNavigationButton>
              </>
            )}
          </ModalWrapper>
        </Modal>
      </>
    );
  }

  // Fallback to logo when no image
  return (
    <LogoContainer>
      <Logo src={bLogo} alt="Baemingo Logo" />
    </LogoContainer>
  );
};

export default ReleaseImage;
